export type PrinterKind = 'system' | 'xprinter';
export type TsplPrinterModel = 'xp365' | 'hzzt9260';
export type LabelPosition = 'edge' | 'center';

export interface PrinterProfile {
  kind: PrinterKind;
  address: string;
  deviceName: string;
  inverted: boolean;
  density: string;
  speed: string;
  feedMm: number;
  /** TSPL devices need the physical gap, not merely a raster height. */
  gapMm: number;
  /** Fine sensor correction: TSPL's second ``GAP`` parameter. */
  gapOffsetMm: number;
  /** A 4-inch head needs a different horizontal origin from the XP-365B. */
  model: TsplPrinterModel;
  /** Physical media, set by the direct-printer calibration assistant. */
  labelWidthMm: number;
  labelHeightMm: number;
  labelPosition: LabelPosition;
  headWidthMm: number;
  xOffsetMm: number;
  yOffsetMm: number;
  /** Compensate a printer whose effective X/Y pitch differs from 203 dpi. */
  scaleX: number;
  scaleY: number;
}

/**
 * The part of a printer profile which is safe and useful to transfer between
 * workstations.  A Bluetooth address names a particular radio on one device;
 * putting it in a QR code would both leak it and make the imported profile
 * fail on the next phone.
 */
export interface SavedPrinterProfileSettings {
  inverted: boolean;
  density: string;
  speed: string;
  feedMm: number;
  gapMm: number;
  gapOffsetMm: number;
  model: TsplPrinterModel;
  labelWidthMm: number;
  labelHeightMm: number;
  labelPosition: LabelPosition;
  headWidthMm: number;
  xOffsetMm: number;
  yOffsetMm: number;
  scaleX: number;
  scaleY: number;
}

export const defaultPrinterProfile = (): PrinterProfile => ({
  kind: 'xprinter', address: '', deviceName: '', inverted: false,
  density: '', speed: '', feedMm: 0, gapMm: 2, gapOffsetMm: 0, model: 'xp365',
  labelWidthMm: 0, labelHeightMm: 0, labelPosition: 'edge', headWidthMm: 0,
  xOffsetMm: 0, yOffsetMm: 0, scaleX: 1, scaleY: 1,
});

const key = (templateId: string) => `storageapp.labels.printer.${templateId}`;
const tapeKey = (templateId: string) => `storageapp.labels.tape-printer.${templateId}`;
const tapeInvertKey = (templateId: string, kind: TapePrinterKind) =>
  `storageapp.labels.tape-inverted.${templateId}.${kind}`;

export function loadPrinterProfile(templateId: string): PrinterProfile {
  try {
    const saved = JSON.parse(localStorage.getItem(key(templateId)) ?? '{}') as Partial<PrinterProfile>;
    return { ...defaultPrinterProfile(), ...saved };
  } catch { return defaultPrinterProfile(); }
}

export function savePrinterProfile(templateId: string, profile: PrinterProfile): void {
  try { localStorage.setItem(key(templateId), JSON.stringify(profile)); } catch { /* private/blocked storage */ }
}

export function savedPrinterProfileSettings(profile: PrinterProfile): SavedPrinterProfileSettings {
  const { inverted, density, speed, feedMm, gapMm, gapOffsetMm, model, labelWidthMm, labelHeightMm, labelPosition, headWidthMm, xOffsetMm, yOffsetMm, scaleX, scaleY } = profile;
  return { inverted, density, speed, feedMm, gapMm, gapOffsetMm, model, labelWidthMm, labelHeightMm, labelPosition, headWidthMm, xOffsetMm, yOffsetMm, scaleX, scaleY };
}

/** Apply a QR profile while keeping this device's paired Bluetooth printer. */
export function applySavedPrinterProfile(profile: PrinterProfile, saved: SavedPrinterProfileSettings): PrinterProfile {
  return { ...profile, ...saved, kind: 'xprinter' };
}

export type TapePrinterKind = 'marklife' | 'niimbot';

export function loadTapePrinterKind(templateId: string): TapePrinterKind {
  try { return localStorage.getItem(tapeKey(templateId)) === 'niimbot' ? 'niimbot' : 'marklife'; } catch { return 'marklife'; }
}

export function saveTapePrinterKind(templateId: string, kind: TapePrinterKind): void {
  try { localStorage.setItem(tapeKey(templateId), kind); } catch { /* private/blocked storage */ }
}

/** Polarity is a property of this template and physical printer, rather than
 * the current dialog: a P15 and D110 can need different settings. */
export function loadTapeInverted(templateId: string, kind: TapePrinterKind): boolean {
  try { return localStorage.getItem(tapeInvertKey(templateId, kind)) === '1'; } catch { return false; }
}

export function saveTapeInverted(templateId: string, kind: TapePrinterKind, inverted: boolean): void {
  try { localStorage.setItem(tapeInvertKey(templateId, kind), inverted ? '1' : '0'); } catch { /* private/blocked storage */ }
}

export function loadLastTemplate(entityKind: string): string {
  try { return localStorage.getItem(`storageapp.labels.template.${entityKind}`) ?? ''; } catch { return ''; }
}

export function saveLastTemplate(entityKind: string, templateId: string): void {
  try { localStorage.setItem(`storageapp.labels.template.${entityKind}`, templateId); } catch { /* private/blocked storage */ }
}
